import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { Tag } from 'entities/Tags/model/Tag'
import { Segment } from 'entities/Segment/model/Segment'
import { Contact } from 'entities/Contacts/model/Contact'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { EnrollmentContactsTableStore } from 'widgets/ContactEnrollment/ui/EnrollmentContactsTable'
import { type ISearchTypeItems, SearchDropdownStore, SearchTypesEnum } from 'widgets/SearchDropdown'
import {
  EnrollmentSearchContent,
  EnrollmentSearchTrigger,
} from 'widgets/ContactEnrollment/ui/EnrollmentSearch'
import { EnrollmentPreviewStore } from 'widgets/ContactEnrollment/store/enrollmentPreview'
import type { ISearchDropdownStoreConfig } from 'widgets/SearchDropdown/store/types'
import { type IEnrollmentInitProps } from 'widgets/ContactEnrollment/store/types'
import { EnrollmentItemType, type IEnrollmentItem } from '../types/types'
import { EnrollmentFilters } from '../models'
import { getTotalCount } from '../utils/contactsCount'

type IEnrollmentListStoreConfig = {
  searchDropdownConfig?: ISearchDropdownStoreConfig
  excludeSearchDropdownConfig?: ISearchDropdownStoreConfig
}

export class EnrollmentListStore {
  private _loading = false
  private _totalCount = 0
  private _totalInvalidCount = 0
  private _countPromise: Promise<void> = Promise.resolve()
  private _disposeCount: IReactionDisposer | null = null
  private _disposeFilters: IReactionDisposer | null = null
  private _filters: EnrollmentFilters = EnrollmentFilters.Empty()
  private _excludeFilters: EnrollmentFilters = EnrollmentFilters.Empty()
  private _number_id: number | null = null
  private readonly _preview: EnrollmentPreviewStore
  private readonly _excludePreview: EnrollmentPreviewStore
  private readonly _searchDropdownStore: SearchDropdownStore
  private readonly _excludeSearchDropdownStore: SearchDropdownStore
  private readonly _enrollmentContactsTableStore: EnrollmentContactsTableStore

  constructor(private _entities: IEntitiesStore, private _config?: IEnrollmentListStoreConfig) {
    this._preview = new EnrollmentPreviewStore({
      removeItem: (item) => this.removeItem(item),
    })
    this._excludePreview = new EnrollmentPreviewStore({
      removeItem: (item) => this.removeExcludeItem(item),
    })

    const dropdownProps = {
      width: 260,
      PopperProps: {
        modifiers: [
          {
            name: 'offset',
            options: {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              offset: ({ reference }) => {
                return [-4, -reference.height - 8]
              },
            },
          },
        ],
      },
      placement: 'bottom-start' as const,
    }

    this._searchDropdownStore = new SearchDropdownStore(
      {
        includeItemsType: [SearchTypesEnum.tags, SearchTypesEnum.segment, SearchTypesEnum.contacts],
        dropdownTriggerComponent: (open) => <EnrollmentSearchTrigger open={open} />,
        dropdownCustomComponent: (onCloseMenu, dropdownId) => (
          <EnrollmentSearchContent onCloseMenu={onCloseMenu} dropdownId={dropdownId} />
        ),
        dropdownProps,
        placeholder: 'Search tag, segment or contact',
        ...this._config?.searchDropdownConfig,
      },
      this._entities
    )
    this._excludeSearchDropdownStore = new SearchDropdownStore(
      {
        includeItemsType: [SearchTypesEnum.tags, SearchTypesEnum.segment, SearchTypesEnum.contacts],
        dropdownTriggerComponent: (open) => <EnrollmentSearchTrigger open={open} />,
        dropdownCustomComponent: (onCloseMenu, dropdownId) => (
          <EnrollmentSearchContent onCloseMenu={onCloseMenu} dropdownId={dropdownId} />
        ),
        dropdownProps,
        placeholder: 'Search tag, segment or contact',
        ...this._config?.excludeSearchDropdownConfig,
      },
      this._entities
    )
    this._enrollmentContactsTableStore = new EnrollmentContactsTableStore(this._entities)
    this._searchDropdownStore.initOptions({
      toggleTypeItem: this.toggleTypeItem,
      selectedMode: false,
    })
    this._excludeSearchDropdownStore.initOptions({
      toggleTypeItem: this.toggleExcludeTypeItem,
      selectedMode: false,
    })

    makeAutoObservable(this)
  }

  setLoading = (status: boolean) => {
    this._loading = status
  }

  get loading() {
    return this._loading
  }

  get totalCount() {
    return this._totalCount
  }

  get totalInvalidCount() {
    return this._totalInvalidCount
  }

  get allTotalCount() {
    return this._totalCount + this._totalInvalidCount
  }

  get allTotalSize() {
    return this._excludeFilters.allSize + this._filters.allSize
  }

  get filters() {
    return this._filters
  }

  get allFiltersWithCount() {
    return this._filters.allWithCount
  }

  get excludeFilters() {
    return this._excludeFilters
  }

  get allExcludeFiltersWithCount() {
    return this._excludeFilters.allWithCount
  }

  get existHubspotFilter() {
    return this._filters.filtersWithCount.some((count) => count.item.hasHubspotFilter)
  }

  get hasHubspotFilterSelected() {
    return !!this.hubspotFiltersCount
  }

  get hubspotFiltersCount() {
    return this._filters.filtersWithCount.reduce((count, item) => {
      return count + item.item.hubspotFiltersCount
    }, 0)
  }

  get preview() {
    return this._preview
  }

  get excludePreview() {
    return this._excludePreview
  }

  get contactsTableStore() {
    return this._enrollmentContactsTableStore
  }

  get searchDropdownStore() {
    return this._searchDropdownStore
  }

  get excludeSearchDropdownStore() {
    return this._excludeSearchDropdownStore
  }

  get emptyResult() {
    return Boolean(this.allTotalSize && this.allTotalCount === 0)
  }

  get selectTitle() {
    const count = this.allTotalCount

    if (count === 1) return 'Select contact'
    if (count > 1) return `Select ${count} contacts`
    if (this.emptyResult) return 'Select 0 contacts'

    return 'Select contacts'
  }

  toggleTypeItem = (item: ISearchTypeItems) => {
    if (item instanceof Contact) {
      this.addOrUpdateItem({
        type: EnrollmentItemType.Contact,
        payload: item,
      })
    }

    if (item instanceof Tag) {
      this.addOrUpdateItem({
        type: EnrollmentItemType.Tag,
        payload: item,
      })
    }

    if (item instanceof Segment) {
      this.addOrUpdateItem({
        type: EnrollmentItemType.Segment,
        payload: item,
      })
    }

    this._searchDropdownStore.setIsShowMore(true)
  }

  toggleExcludeTypeItem = (item: ISearchTypeItems) => {
    if (item instanceof Contact) {
      this.addOrUpdateExcludeItem({
        type: EnrollmentItemType.Contact,
        payload: item,
      })
    }

    if (item instanceof Tag) {
      this.addOrUpdateExcludeItem({
        type: EnrollmentItemType.Tag,
        payload: item,
      })
    }

    if (item instanceof Segment) {
      this.addOrUpdateExcludeItem({
        type: EnrollmentItemType.Segment,
        payload: item,
      })
    }

    this._excludeSearchDropdownStore.setIsShowMore(true)
  }

  init = (
    filters: EnrollmentFilters,
    number_id?: number | null,
    callBack?: IEnrollmentInitProps['confirmEnrollment'],
    excludeFilters?: EnrollmentFilters
  ) => {
    this._disposeCount?.()
    this._disposeFilters?.()

    this._filters = filters
    this._excludeFilters = excludeFilters || EnrollmentFilters.Empty()
    this._number_id = number_id || null

    this._disposeCount = reaction(
      (): [Contact[], Segment[], Segment[], Tag[], Contact[], Segment[], Segment[], Tag[]] => [
        this._filters.contacts,
        this._filters.segments,
        this._filters.filters,
        this._filters.tags,
        this._excludeFilters.contacts,
        this._excludeFilters.segments,
        this._excludeFilters.filters,
        this._excludeFilters.tags,
      ],
      ([
        contacts,
        segments,
        filters,
        tags,
        excludeContacts,
        excludeSegments,
        excludeFilters,
        excludeTags,
      ]) => {
        this._loading = true

        this._countPromise = getTotalCount({
          contacts,
          segments,
          filters,
          tags,
          excludeContacts,
          excludeSegments,
          excludeFilters,
          excludeTags,
          number_id: this._number_id,
        }).then(({ count, invalid_count, invalid_breakdown }) =>
          runInAction(() => {
            this._totalCount = count
            this._totalInvalidCount = invalid_count

            this._loading = false
            this._enrollmentContactsTableStore.setTotal(count + invalid_count)

            callBack?.({
              total: count,
              skipped: invalid_count,
              invalid_breakdown: invalid_breakdown,
            })
          })
        )
      },
      {
        fireImmediately: true,
      }
    )

    this._disposeFilters = reaction(
      (): [EnrollmentFilters, EnrollmentFilters] => [this._filters, this._excludeFilters],
      ([filters, excludeFilters]) => {
        this._enrollmentContactsTableStore.setFilter(filters, excludeFilters)
      },
      {
        fireImmediately: true,
      }
    )
  }

  waitForCount = () => this._countPromise

  reset = () => {
    this._disposeCount?.()
    this._disposeFilters?.()
    this._filters = EnrollmentFilters.Empty()
    this._excludeFilters = EnrollmentFilters.Empty()
    this._totalCount = 0
    this._totalInvalidCount = 0
    this._preview.reset()
    this._excludePreview.reset()
    this._enrollmentContactsTableStore.reset()
  }

  removeItem = (item: IEnrollmentItem) => {
    switch (item.type) {
      case EnrollmentItemType.Contact:
        return this._filters.deleteContact(item.payload.id)
      case EnrollmentItemType.Tag:
        return this._filters.deleteTag(item.payload.id)
      case EnrollmentItemType.Segment:
        return this._filters.deleteSegment(item.payload.id)
      case EnrollmentItemType.Filter:
        return this._filters.deleteFilter(item.payload.id)
    }
  }

  addOrUpdateItem = (item: IEnrollmentItem) => {
    switch (item.type) {
      case EnrollmentItemType.Contact:
        return this._filters.addOrUpdateContact(item.payload)
      case EnrollmentItemType.Tag:
        return this._filters.addOrUpdateTag(item.payload)
      case EnrollmentItemType.Segment:
        return this._filters.addOrUpdateSegment(item.payload)
      case EnrollmentItemType.Filter:
        return this._filters.addOrUpdateFilter(item.payload)
    }
  }

  removeExcludeItem = (item: IEnrollmentItem) => {
    switch (item.type) {
      case EnrollmentItemType.Contact:
        return this._excludeFilters.deleteContact(item.payload.id)
      case EnrollmentItemType.Tag:
        return this._excludeFilters.deleteTag(item.payload.id)
      case EnrollmentItemType.Segment:
        return this._excludeFilters.deleteSegment(item.payload.id)
      case EnrollmentItemType.Filter:
        return this._excludeFilters.deleteFilter(item.payload.id)
    }
  }

  addOrUpdateExcludeItem = (item: IEnrollmentItem) => {
    switch (item.type) {
      case EnrollmentItemType.Contact:
        return this._excludeFilters.addOrUpdateContact(item.payload)
      case EnrollmentItemType.Tag:
        return this._excludeFilters.addOrUpdateTag(item.payload)
      case EnrollmentItemType.Segment:
        return this._excludeFilters.addOrUpdateSegment(item.payload)
      case EnrollmentItemType.Filter:
        return this._excludeFilters.addOrUpdateFilter(item.payload)
    }
  }
}
